/**
 * Globales, NICHT wegklickbares Banner fuer eine aktive Impersonation (P3,
 * docs/KONZEPT-ADMIN-PANEL.md § "Impersonation — Mechanismus", Punkt 6 "Frontend-Banner").
 *
 * Sitzt in `ProtectedLayout` (App.tsx), also auf JEDER Seite der normalen App - genau dort, wohin
 * der Admin nach dem Start der Impersonation weitergeleitet wird (tenant-detail.tsx) und wo er
 * anschliessend als Owner des fremden Mandanten arbeitet (Konzept: "der Admin agiert danach
 * INNERHALB der normalen App").
 *
 * ══ AKTIV-ERKENNUNG: SERVERSEITIGE BESTAETIGUNG, KEIN CLIENT-ZUSTAND ═══════════════════════════
 * Fragt `GET /api/admin/impersonation/aktiv` beim Mounten und danach alle 20s erneut ab (Muster
 * `hooks/useApi.ts` + eigenes Intervall, wie andere periodisch pollende Stellen im Repo). KEIN
 * Local-Storage-Marker: die Antwort dieses Endpunkts IST bereits die serverseitige Bestaetigung
 * (er liest live aus `impersonation_grants`/`tenants` ueber `withPlatformAdmin`) - ein zusaetzlicher
 * Client-Marker wuerde hier nichts pruefbar machen, was die Server-Antwort nicht ohnehin schon
 * liefert, sondern nur einen zweiten, potenziell veralteten Zustand einfuehren. Fuer die
 * uebergrosse Mehrheit der Aufrufer (normale Mandanten-Nutzer, keine Platform-Admins) antwortet
 * der Endpunkt einheitlich `{ data: null }` - dasselbe Aussehen wie "Admin ohne aktiven Grant",
 * kein Existenz-Orakel (siehe Kommentar in `routes/admin.ts`).
 *
 * ══ NICHT WEGKLICKBAR ═══════════════════════════════════════════════════════════════════════
 * Es gibt keinen Schliessen-Knopf, der das Banner nur visuell versteckt. Die EINZIGE Moeglichkeit,
 * es verschwinden zu lassen, ist "Beenden" - das ruft tatsaechlich
 * `POST /api/admin/impersonation/:grantId/end` auf und blendet erst nach dessen Erfolg aus.
 *
 * ══ LAYOUT ══════════════════════════════════════════════════════════════════════════════════
 * Fixiert am oberen Rand, ab `lg` mit demselben linken Versatz wie Header/Main (`--nav-w`) - liegt
 * damit NEBEN der Sidebar, nicht darueber. Die tatsaechliche Hoehe wird per `ResizeObserver`
 * gemessen und in `--impersonation-banner-h` geschrieben (Default `0px`, wenn nichts aktiv ist) -
 * `AppShell` verschiebt Header/Main um genau diesen Wert nach unten (siehe dortiger Kommentar),
 * sodass ohne aktives Banner exakt das bisherige Layout entsteht.
 */
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useApi, apiMutate } from "../hooks/useApi";
import { formatDateTime } from "./format";
import { Icon } from "./components";
import { urlFuerSubdomain } from "../lib/subdomain";

interface AktiveImpersonation {
  grantId: string;
  tenantId: string;
  tenantName: string;
  tenantSlug: string;
  reason: string;
  startedAt: string;
  expiresAt: string;
}

const POLL_INTERVALL_MS = 20_000;

/** "endet in 12 Min" / "endet in < 1 Min" - grobe Minutenangabe reicht fuer ein Support-Banner,
 *  kein sekundengenauer Countdown noetig (der naechste Poll frischt den Wert ohnehin auf). */
function restzeitText(expiresAt: string): string {
  const restMs = new Date(expiresAt).getTime() - Date.now();
  if (restMs <= 0) return "läuft gleich ab";
  const restMin = Math.ceil(restMs / 60_000);
  return restMin <= 1 ? "endet in < 1 Min" : `endet in ${restMin} Min`;
}

export function ImpersonationBanner() {
  const { data, reload } = useApi<AktiveImpersonation | null>("/api/admin/impersonation/aktiv");
  const [beendenBusy, setBeendenBusy] = useState(false);
  const [beendenError, setBeendenError] = useState<string | null>(null);
  // Sofort nach erfolgreichem Beenden ausblenden, statt nur auf die anschliessende Navigation zu
  // vertrauen (die im echten Browser die ganze App neu laedt, aber nicht sofort synchron
  // geschieht) - "Banner verschwindet" ist damit eine direkte Folge des erfolgreichen Aufrufs,
  // nicht nur ein Nebeneffekt der Weiterleitung.
  const [beendet, setBeendet] = useState(false);
  const bannerRef = useRef<HTMLDivElement>(null);

  // Periodisches Nachfragen - dieselbe Bestaetigung wie beim ersten Laden, kein separater
  // Client-Zustand (siehe Dateikopf). `reload` kommt aus `useApi` (hooks/useApi.ts).
  useEffect(() => {
    const id = window.setInterval(reload, POLL_INTERVALL_MS);
    return () => window.clearInterval(id);
  }, [reload]);

  // Tatsaechliche Hoehe messen und als CSS-Variable bereitstellen, statt eine feste Pixelzahl zu
  // raten (der Text kann auf schmalen Bildschirmen umbrechen) - Vorbild: `--copilot-pinned-w` in
  // `ui/AppShell.tsx` (`useLayoutEffect` VOR dem ersten Paint, kein sichtbarer Sprung).
  const sichtbar = Boolean(data) && !beendet;

  useLayoutEffect(() => {
    const root = document.documentElement;
    const el = bannerRef.current;
    if (!sichtbar || !el) {
      root.style.setProperty("--impersonation-banner-h", "0px");
      return;
    }
    const setzeHoehe = () => root.style.setProperty("--impersonation-banner-h", `${el.offsetHeight}px`);
    setzeHoehe();
    const ro = new ResizeObserver(setzeHoehe);
    ro.observe(el);
    return () => {
      ro.disconnect();
      root.style.setProperty("--impersonation-banner-h", "0px");
    };
  }, [sichtbar]);

  if (!sichtbar || !data) return null;

  async function beenden() {
    setBeendenBusy(true);
    setBeendenError(null);
    try {
      await apiMutate(`/api/admin/impersonation/${data!.grantId}/end`, "POST");
      setBeendet(true);
      // Zurueck zur Admin-Oberflaeche (Konzept § Impersonation, Punkt 3: "sinnvollerweise zurueck
      // zur Admin-Oberflaeche navigieren").
      window.location.assign(urlFuerSubdomain("maestro"));
    } catch (err) {
      setBeendenError(err instanceof Error ? err.message : String(err));
      setBeendenBusy(false);
    }
  }

  return (
    <div
      ref={bannerRef}
      role="alert"
      className="fixed inset-x-0 top-0 z-[70] flex flex-wrap items-center gap-x-4 gap-y-1.5 border-b border-[#7A2E14] bg-[#9A3412] px-4 py-2 text-inverse-on-surface lg:left-[var(--nav-w)]"
    >
      <div className="flex items-center gap-2">
        <Icon name="visibility" className="text-[18px]" />
        <span className="font-body-sm text-body-sm font-semibold">
          Support-Zugriff auf {data.tenantName} <span className="font-mono opacity-80">({data.tenantSlug})</span>
        </span>
      </div>
      <span className="font-body-sm text-[12px] opacity-90">
        seit {formatDateTime(data.startedAt)} · {restzeitText(data.expiresAt)}
      </span>
      <span className="min-w-0 flex-1 truncate font-body-sm text-[12px] italic opacity-90" title={data.reason}>
        Grund: {data.reason}
      </span>
      {beendenError && <span className="font-body-sm text-[12px] text-[#FFD9CC]">{beendenError}</span>}
      <button
        type="button"
        onClick={() => void beenden()}
        disabled={beendenBusy}
        className="ml-auto shrink-0 rounded-lg bg-white/15 px-3 py-1.5 font-label-md text-label-md font-semibold text-inverse-on-surface transition-colors hover:bg-white/25 disabled:opacity-60"
      >
        {beendenBusy ? "Beendet …" : "Beenden"}
      </button>
    </div>
  );
}
